import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useUser } from "../context/UserContext";
import { useApi } from "../utils/Api";
import { buildPhone, isValidPhone } from "../utils/phone";

// Spinner overlay
const SpinnerOverlay = () => (
  <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999]">
    <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
  </div>
);

const AuthModal = ({ onClose }) => {
  const { setUser } = useUser();
  const { apiFetch } = useApi();

  const [mode, setMode] = useState("login");
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login
  const [loginPhone, setLoginPhone] = useState("");
  const [loginCountry, setLoginCountry] = useState("+44");

  // Signup
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupCountry, setSignupCountry] = useState("+44");
  const [signupEmail, setSignupEmail] = useState("");

  // Welcome
  const [welcomeScreen, setWelcomeScreen] = useState(false);
  const [loginUser, setLoginUser] = useState(null);

  // The login is already saved by UserContext; this is a belt-and-braces
  // copy, and a browser that blocks storage must not turn a successful
  // login into an error.
  const safeStore = (key, value) => {
    try { localStorage.setItem(key, value); } catch { /* storage blocked */ }
  };

  const goToLogin = () => { setError(""); setMode("login"); setShowSignupForm(false); };
  const goToSignup = () => { setError(""); setMode("signup"); setShowSignupForm(false); };
  const goToSignupForm = () => { setError(""); setShowSignupForm(true); };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidPhone(loginCountry, loginPhone)) { setError("Please enter a valid phone number"); return; }
    setLoading(true);
    try {
      const fullPhone = buildPhone(loginCountry, loginPhone);
      const data = await apiFetch("customer-login", { phone: fullPhone });
      setLoading(false);
      if (data.status === "success") {
        const userWithToken = { ...data.customer, token: data.token };
        setUser(userWithToken);
        setLoginUser(userWithToken);
        safeStore("user", JSON.stringify(userWithToken));
        safeStore("authTokenExpires", data.expiresAt);
        setWelcomeScreen(true);
        setTimeout(() => { setWelcomeScreen(false); onClose(); }, 2000);
      } else if (data.error === "User does not exist") {
        setError("We couldn't find an account with that number. Check the number or sign up instead.");
      } else {
        setError("We couldn't log you in right now. Please try again in a moment.");
      }
    } catch {
      setError("We couldn't reach the server. Check your connection and try again.");
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidPhone(signupCountry, signupPhone)) { setError("Please enter a valid phone number"); return; }
    setLoading(true);
    try {
      const payload = { first_name: signupFirstName, last_name: signupLastName, phone: buildPhone(signupCountry, signupPhone), email: signupEmail };
      const data = await apiFetch("customer-signup", payload);
      setLoading(false);
      if (data.status === "success") {
        const userWithToken = { ...data.customer, token: data.token };
        setUser(userWithToken);
        setLoginUser(userWithToken);
        safeStore("user", JSON.stringify(userWithToken));
        safeStore("authTokenExpires", data.expiresAt);
        setWelcomeScreen(true);
        setTimeout(() => { setWelcomeScreen(false); onClose(); }, 2000);
      } else {
        setError(data.error || "Signup failed");
      }
    } catch {
      setError("We couldn't reach the server. Check your connection and try again.");
      setLoading(false);
    }
  };

  // Shared input class
  const inputCls = "w-full px-3.5 py-3 mb-4 rounded-lg border border-[#ccc] text-sm text-black transition-[border,box-shadow] duration-200 font-[inherit] focus:outline-none focus:border-brand-orange focus:shadow-[0_0_8px_rgba(250,147,36,0.3)]";
   const phoneSelectCls = "w-[80px] px-2 py-3 rounded-md border border-[#ccc] text-sm h-11 box-border font-[inherit] text-black bg-[#f9f9f9] cursor-pointer shrink-0";

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[9999] backdrop-blur-sm animate-fade-in">
      {loading && <SpinnerOverlay />}
      <div
        className="bg-white px-8 max-[360px]:px-5 py-10 rounded-xl w-[380px] max-w-[90%] shadow-[0_10px_40px_rgba(0,0,0,0.25)] relative animate-slide-up text-center transition-all duration-300 text-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <span
          className="absolute top-[15px] right-[18px] text-[30px] cursor-pointer text-[#333] font-bold transition-[color,transform] duration-200 hover:text-[#d63447] hover:rotate-90"
          onClick={onClose}
        >
          &times;
        </span>

        {welcomeScreen && loginUser ? (
          <div className="text-center py-5">
            <h2 className="text-black font-semibold">Welcome, {loginUser.first_name || "Guest"}!</h2>
            <p className="mt-2.5 text-gray-600">You're now logged in.</p>
          </div>
        ) : (
          <>
            {mode === "login" ? (
              <form onSubmit={handleLogin} className="text-left">
                <div className="text-center mb-5">
                  <h2 className="text-2xl font-semibold text-[#222]">Welcome Back!</h2>
                  <p className="mt-2 mb-5 text-sm text-[#666]">Login with your phone number.</p>
                </div>

                <div className="flex gap-2 mb-4">
                  <select value={loginCountry} onChange={(e) => setLoginCountry(e.target.value)} className={phoneSelectCls}>
                    <option value="+44">+44</option>
                    <option value="+234">+234</option>
                  </select>
                  <input type="tel" placeholder="Phone Number" value={loginPhone} maxLength={15}
                    onChange={(e) => setLoginPhone(e.target.value.replace(/\D/g, ""))} required
                    className="flex-1 min-w-0 px-3.5 py-3 rounded-md border border-[#ccc] text-sm text-black h-11 box-border font-[inherit] focus:outline-none focus:border-brand-orange" />
                </div>

                {error && <p className="text-[#d63447] text-base font-bold mb-3 text-center">{error}</p>}
                <button type="submit" className="w-full py-3 bg-brand-orange text-white border-none rounded-md font-semibold text-base cursor-pointer transition-all duration-200 hover:opacity-70 hover:-translate-y-0.5 font-[inherit]">Login</button>

                <p className="mt-3.5 text-sm text-[#555] text-center">
                  Don't have an account?{" "}
                  <span className="text-brand-orange font-semibold cursor-pointer underline hover:text-[#b52a3d] transition-colors" onClick={goToSignup}>Sign Up</span>
                </p>
              </form>
            ) : !showSignupForm ? (
              <div className="text-left">
                <h2 className="text-xl font-semibold text-[#222] text-center mb-2">Why Sign Up?</h2>
                <ul className="list-none p-0 mt-5 mb-5 text-left">
                  {["📦 View all your previous orders", "📍 Track your orders in real time", "🔄 Reorder your previous orders in one click", "🎁 Receive updates on promos and discounts"].map((item, i) => (
                    <li key={i} className="flex items-center mb-3 text-base text-[#555]">{item}</li>
                  ))}
                </ul>
                <button className="block w-full bg-[#ff6b01] text-white border-none py-3 text-base rounded cursor-pointer transition-colors duration-300 hover:bg-[#ff8500] font-[inherit]" onClick={goToSignupForm}>
                  Proceed to Sign Up
                </button>
                <p className="mt-3 text-sm text-[#555] text-center">
                  Already have an account?{" "}
                  <span className="text-brand-orange font-semibold cursor-pointer underline hover:text-[#b52a3d] transition-colors" onClick={goToLogin}>Login</span>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSignup} className="text-left">
                <div className="text-center mb-5">
                  <h2 className="text-2xl font-semibold text-[#222]">Create Account</h2>
                  <p className="mt-2 mb-5 text-sm text-[#666]">Sign up to get started.</p>
                </div>
                <input value={signupFirstName} onChange={(e) => setSignupFirstName(e.target.value)} placeholder="First Name" required className={inputCls} />
                <input value={signupLastName} onChange={(e) => setSignupLastName(e.target.value)} placeholder="Last Name" required className={inputCls} />
                <div className="flex gap-2 mb-4">
                  <select value={signupCountry} onChange={(e) => setSignupCountry(e.target.value)} className={phoneSelectCls}>
                    <option value="+44">+44</option>
                    <option value="+234">+234</option>
                  </select>
                  <input type="tel" value={signupPhone} maxLength={15} onChange={(e) => setSignupPhone(e.target.value.replace(/\D/g, ""))} placeholder="Phone Number" required
                    className="flex-1 min-w-0 px-3.5 py-3 rounded-md border border-[#ccc] text-sm text-black h-11 box-border font-[inherit] focus:outline-none focus:border-brand-orange" />
                </div>
                <input type="email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} placeholder="Email" required className={inputCls} />
                {error && <p className="text-[#d63447] text-base font-bold mb-3 text-center">{error}</p>}
                <button type="submit" className="w-full py-3 bg-brand-orange text-white border-none rounded-md font-semibold text-base cursor-pointer transition-all duration-200 hover:opacity-70 hover:-translate-y-0.5 font-[inherit]">Sign Up</button>
                <p className="mt-3.5 text-sm text-[#555] text-center">
                  Already have an account?{" "}
                  <span className="text-brand-orange font-semibold cursor-pointer underline hover:text-[#b52a3d] transition-colors" onClick={goToLogin}>Login</span>
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AuthModal;